import React, { useState, useEffect } from 'react';
import { api, fmt } from '../utils/api';
import LogoMark from './LogoMark';
import LegalModal from './LegalModal';
import { trackEvent } from '../utils/analytics';

export default function AuthPage({ onLogin }) {
  const [mode, setMode] = useState('login'); // 'login' | 'register'
  const [joinMode, setJoinMode] = useState(false); // when registering: create new vs join existing
  const [form, setForm] = useState({
    email: '',
    password: '',
    name: '',
    householdName: '',
    annualBudget: '130000',
    joinHouseholdId: '',
    agreedToTerms: false,
    annualIncome: '',
    filingStatus: 'single',
    state: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [legalDoc, setLegalDoc] = useState(null); // 'terms' | 'privacy' | null

  const [states, setStates] = useState({});
  const [manualBudgetMode, setManualBudgetMode] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [estimating, setEstimating] = useState(false);

  useEffect(() => {
    api.getStates().then(setStates).catch(() => {});
  }, []);

  const getEstimate = async () => {
    if (!form.annualIncome || !form.state) {
      setError('Enter your income and state first.');
      return;
    }
    setError('');
    setEstimating(true);
    try {
      const data = await api.estimateBudget({
        annualIncome: parseFloat(form.annualIncome),
        filingStatus: form.filingStatus,
        state: form.state,
      });
      setEstimate(data);
    } catch (err) {
      setError('Could not calculate estimate. Try again.');
    } finally {
      setEstimating(false);
    }
  };

  // 2FA login gate
  const [awaiting2FA, setAwaiting2FA] = useState(false);
  const [pendingToken, setPendingToken] = useState(null);
  const [twoFACode, setTwoFACode] = useState('');

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const finishLogin = (data, isNewSignup = false) => {
    localStorage.setItem('findash_token', data.token);
    onLogin(data.user, isNewSignup);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (mode === 'login') {
        const data = await api.login(form.email, form.password);
        if (data.requires2FA) {
          setPendingToken(data.pendingToken);
          setAwaiting2FA(true);
        } else {
          finishLogin(data);
        }
      } else {
        if (!form.agreedToTerms) {
          setError('You must agree to the Terms of Service to continue.');
          setLoading(false);
          return;
        }
        await api.register({
          email: form.email,
          password: form.password,
          name: form.name,
          agreedToTerms: 'true',
          ...(joinMode
            ? { joinHouseholdId: form.joinHouseholdId.trim() }
            : manualBudgetMode
            ? {
                householdName: form.householdName,
                annualBudget: parseFloat(form.annualBudget),
              }
            : {
                householdName: form.householdName,
                annualIncome: form.annualIncome ? parseFloat(form.annualIncome) : undefined,
                filingStatus: form.filingStatus,
                state: form.state || undefined,
              }),
        });
        // Auto-login after register
        trackEvent('sign_up', { method: joinMode ? 'join_household' : 'new_household' });
        const data = await api.login(form.email, form.password);
        if (data.requires2FA) {
          setPendingToken(data.pendingToken);
          setAwaiting2FA(true);
        } else {
          finishLogin(data, !joinMode);
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const submit2FA = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await api.verify2FALogin(pendingToken, twoFACode.trim());
      finishLogin(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (awaiting2FA) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.logo}>
            <LogoMark size={32} />
            <span style={styles.logoText}>Boring Budgeting</span>
          </div>
          <p style={styles.tagline}>Enter the 6-digit code from your authenticator app</p>

          <form onSubmit={submit2FA} style={styles.form}>
            <input
              style={{ ...styles.input, textAlign: 'center', fontSize: '1.3rem', letterSpacing: '0.3em' }}
              placeholder="000000"
              value={twoFACode}
              onChange={(e) => setTwoFACode(e.target.value)}
              maxLength={12}
              autoComplete="one-time-code"
              inputMode="numeric"
              pattern="[0-9]*"
              autoFocus
              required
            />
            {error && <div style={styles.error}>{error}</div>}
            <button style={styles.btn} type="submit" disabled={loading}>
              {loading ? '...' : 'Verify'}
            </button>
            <button
              type="button"
              style={styles.linkBtn}
              onClick={() => { setAwaiting2FA(false); setTwoFACode(''); setError(''); }}
            >
              ← Back to sign in
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logo}>
          <LogoMark size={32} />
          <span style={styles.logoText}>Boring Budgeting</span>
        </div>
        <p style={styles.tagline}>The budget that runs itself.</p>
        <p style={styles.taglineSecondary}>Automatic budgeting, zero excitement.</p>

        <div style={styles.tabs}>
          <button
            style={{ ...styles.tab, ...(mode === 'login' ? styles.tabActive : {}) }}
            onClick={() => setMode('login')}
          >
            Sign In
          </button>
          <button
            style={{ ...styles.tab, ...(mode === 'register' ? styles.tabActive : {}) }}
            onClick={() => setMode('register')}
          >
            Create Account
          </button>
        </div>

        <form onSubmit={submit} style={styles.form}>
          {mode === 'register' && (
            <>
              <input
                style={styles.input}
                placeholder="Your name"
                value={form.name}
                onChange={set('name')}
                required
              />

              <div style={styles.subTabs}>
                <button
                  type="button"
                  style={{ ...styles.subTab, ...(!joinMode ? styles.subTabActive : {}) }}
                  onClick={() => setJoinMode(false)}
                >
                  Create New Household
                </button>
                <button
                  type="button"
                  style={{ ...styles.subTab, ...(joinMode ? styles.subTabActive : {}) }}
                  onClick={() => setJoinMode(true)}
                >
                  Join Existing Household
                </button>
              </div>

              {joinMode ? (
                <input
                  style={styles.input}
                  placeholder="Household ID (ask whoever set up Boring Budgeting)"
                  value={form.joinHouseholdId}
                  onChange={set('joinHouseholdId')}
                  required
                />
              ) : (
                <>
                  <input
                    style={styles.input}
                    placeholder="Household name (e.g. The Smiths)"
                    value={form.householdName}
                    onChange={set('householdName')}
                    required
                  />

                  {!manualBudgetMode ? (
                    <>
                      <p style={styles.miniHelp}>
                        Give us your income and state and we'll estimate a
                        reasonable annual budget for you — no math required.
                      </p>
                      <input
                        style={styles.input}
                        type="number"
                        placeholder="Annual household income ($)"
                        value={form.annualIncome}
                        onChange={set('annualIncome')}
                        min="0"
                      />
                      <div style={styles.subTabs}>
                        <button
                          type="button"
                          style={{ ...styles.subTab, ...(form.filingStatus === 'single' ? styles.subTabActive : {}) }}
                          onClick={() => setForm({ ...form, filingStatus: 'single' })}
                        >
                          Single
                        </button>
                        <button
                          type="button"
                          style={{ ...styles.subTab, ...(form.filingStatus === 'married' ? styles.subTabActive : {}) }}
                          onClick={() => setForm({ ...form, filingStatus: 'married' })}
                        >
                          Married
                        </button>
                      </div>
                      <select
                        style={styles.input}
                        value={form.state}
                        onChange={set('state')}
                      >
                        <option value="">Select your state...</option>
                        {Object.entries(states).map(([code, stateName]) => (
                          <option key={code} value={code}>{stateName}</option>
                        ))}
                      </select>

                      <button type="button" style={styles.estimateBtn} onClick={getEstimate} disabled={estimating}>
                        {estimating ? 'Calculating...' : 'Get Budget Estimate'}
                      </button>

                      {estimate && (
                        <div style={styles.estimateBox}>
                          <div style={styles.estimateRow}>
                            <span>Federal tax (est.)</span>
                            <span>{fmt.currency(estimate.federalTax)}</span>
                          </div>
                          <div style={styles.estimateRow}>
                            <span>State tax (est.)</span>
                            <span>{fmt.currency(estimate.stateTax)}</span>
                          </div>
                          <div style={styles.estimateRow}>
                            <span>FICA (SS + Medicare)</span>
                            <span>{fmt.currency(estimate.ficaTax)}</span>
                          </div>
                          <div style={styles.estimateDivider} />
                          <div style={{ ...styles.estimateRow, fontWeight: '700' }}>
                            <span style={{ color: '#fff' }}>Suggested annual budget</span>
                            <span style={{ color: '#00d4aa' }}>{fmt.currency(estimate.netIncome)}</span>
                          </div>
                          <div style={styles.estimateNote}>
                            Based on estimated take-home pay ({estimate.effectiveRate}% effective
                            tax rate). This is a planning estimate, not tax advice — you can
                            adjust your budget anytime after signing up.
                          </div>
                        </div>
                      )}

                      <button
                        type="button"
                        style={styles.linkBtn}
                        onClick={() => setManualBudgetMode(true)}
                      >
                        I'll set my own budget instead
                      </button>
                    </>
                  ) : (
                    <>
                      <input
                        style={styles.input}
                        type="number"
                        placeholder="Annual budget ($)"
                        value={form.annualBudget}
                        onChange={set('annualBudget')}
                        min="1000"
                        required
                      />
                      <button
                        type="button"
                        style={styles.linkBtn}
                        onClick={() => setManualBudgetMode(false)}
                      >
                        ← Use the income estimator instead
                      </button>
                    </>
                  )}
                </>
              )}
            </>
          )}

          <input
            style={styles.input}
            type="email"
            placeholder="Email address"
            value={form.email}
            onChange={set('email')}
            required
            autoComplete="email"
          />
          <input
            style={styles.input}
            type="password"
            placeholder="Password"
            value={form.password}
            onChange={set('password')}
            required
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
          />

          {mode === 'register' && (
            <label style={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={form.agreedToTerms}
                onChange={(e) => setForm({ ...form, agreedToTerms: e.target.checked })}
              />
              <span>
                I agree to the{' '}
                <button type="button" style={styles.inlineLink} onClick={() => setLegalDoc('terms')}>
                  Terms of Service
                </button>{' '}
                and{' '}
                <button type="button" style={styles.inlineLink} onClick={() => setLegalDoc('privacy')}>
                  Privacy Policy
                </button>
              </span>
            </label>
          )}

          {error && <div style={styles.error}>{error}</div>}

          <button style={styles.btn} type="submit" disabled={loading}>
            {loading ? '...' : mode === 'login' ? 'Sign In' : 'Create Account'}
          </button>
        </form>
      </div>

      {legalDoc && <LegalModal doc={legalDoc} onClose={() => setLegalDoc(null)} />}
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: '#0a0f1e',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1rem',
    fontFamily: "'Syne', sans-serif",
  },
  card: {
    background: '#111827',
    border: '1px solid #1e2a3a',
    borderRadius: '16px',
    padding: '2.5rem',
    width: '100%',
    maxWidth: '420px',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '0.25rem',
  },
  logoIcon: { fontSize: '1.5rem', color: '#00d4aa' },
  logoText: { fontSize: '1.3rem', fontWeight: '800', color: '#fff', letterSpacing: '-0.02em' },
  tagline: { color: '#e5e7eb', fontSize: '0.92rem', fontWeight: '600', marginBottom: '0.3rem', marginTop: 0 },
  taglineSecondary: { color: '#4b5563', fontSize: '0.75rem', fontStyle: 'italic', marginBottom: '1.75rem', marginTop: 0 },
  tabs: { display: 'flex', gap: '0.25rem', marginBottom: '1.5rem', background: '#0a0f1e', borderRadius: '8px', padding: '4px' },
  tab: {
    flex: 1, padding: '0.5rem', border: 'none', borderRadius: '6px',
    background: 'transparent', color: '#6b7280', cursor: 'pointer',
    fontSize: '0.875rem', fontFamily: "'Syne', sans-serif", fontWeight: '600',
  },
  tabActive: { background: '#1e2a3a', color: '#fff' },
  subTabs: {
    display: 'flex',
    gap: '0.25rem',
    marginBottom: '0.25rem',
  },
  subTab: {
    flex: 1,
    padding: '0.4rem',
    border: '1px solid #1e2a3a',
    borderRadius: '6px',
    background: 'transparent',
    color: '#6b7280',
    cursor: 'pointer',
    fontSize: '0.7rem',
    fontFamily: "'Syne', sans-serif",
    fontWeight: '600',
  },
  subTabActive: {
    background: 'rgba(0, 212, 170, 0.1)',
    borderColor: '#00d4aa',
    color: '#00d4aa',
  },
  form: { display: 'flex', flexDirection: 'column', gap: '0.75rem' },
  input: {
    padding: '0.75rem 1rem',
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '8px',
    color: '#fff',
    fontSize: '0.9rem',
    fontFamily: "'Syne', sans-serif",
    outline: 'none',
  },
  error: {
    background: 'rgba(239,68,68,0.1)',
    border: '1px solid rgba(239,68,68,0.3)',
    color: '#ef4444',
    padding: '0.6rem 1rem',
    borderRadius: '8px',
    fontSize: '0.875rem',
  },
  checkboxRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.5rem',
    color: '#9ca3af',
    fontSize: '0.75rem',
    lineHeight: '1.4',
    fontFamily: "'Syne', sans-serif",
  },
  inlineLink: {
    background: 'none',
    border: 'none',
    color: '#00d4aa',
    cursor: 'pointer',
    fontSize: '0.75rem',
    padding: 0,
    textDecoration: 'underline',
    fontFamily: "'Syne', sans-serif",
  },
  linkBtn: {
    background: 'none',
    border: 'none',
    color: '#6b7280',
    cursor: 'pointer',
    fontSize: '0.8rem',
    padding: '0.25rem',
    fontFamily: "'Syne', sans-serif",
  },
  miniHelp: {
    color: '#6b7280',
    fontSize: '0.75rem',
    lineHeight: '1.4',
    margin: '0 0 0.25rem',
    fontFamily: "'Syne', sans-serif",
  },
  estimateBtn: {
    padding: '0.6rem',
    background: 'transparent',
    color: '#00d4aa',
    border: '1px solid #00d4aa',
    borderRadius: '8px',
    fontSize: '0.85rem',
    fontWeight: '700',
    fontFamily: "'Syne', sans-serif",
    cursor: 'pointer',
  },
  estimateBox: {
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '8px',
    padding: '0.85rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.35rem',
  },
  estimateRow: {
    display: 'flex',
    justifyContent: 'space-between',
    color: '#9ca3af',
    fontSize: '0.8rem',
    fontFamily: "'DM Mono', monospace",
  },
  estimateDivider: { borderTop: '1px solid #1e2a3a', margin: '0.25rem 0' },
  estimateNote: {
    color: '#4b5563',
    fontSize: '0.7rem',
    lineHeight: '1.4',
    marginTop: '0.25rem',
  },
  btn: {
    padding: '0.875rem',
    background: '#00d4aa',
    color: '#0a0f1e',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '700',
    fontFamily: "'Syne', sans-serif",
    cursor: 'pointer',
    marginTop: '0.5rem',
  },
};
